/**
 * @fileoverview Voice Latency Monitor - Performance tracking singleton
 * 
 * @description
 * Tracks and persists latency metrics for voice infrastructure:
 * - Records individual operation timings
 * - Calculates P50/P95/P99 percentiles
 * - Batches database writes every 5 seconds
 * - Auto-flushes on page unload
 * 
 * Phase 11.1: Database persistence for analytics dashboard
 * Phase 11.3: Benchmark targets (simple <50ms, list <100ms, tool <300ms)
 * 
 * @example
 * ```ts
 * recordLatency('simple_command_match', 42);
 * recordLatency('tool_execution', 285);
 * logBenchmarks(); // Shows pass/fail against targets
 * ```
 */

import { supabase } from "@/integrations/supabase/client";

interface LatencyMetric {
  name: string;
  duration: number;
  timestamp: number;
  sessionId?: string;
}

class LatencyMonitor {
  private metrics: LatencyMetric[] = [];
  private maxMetrics = 100;
  private currentSessionId: string | null = null;
  private pendingPersistence: LatencyMetric[] = [];
  private persistenceTimer: NodeJS.Timeout | null = null;

  setSessionId(sessionId: string) {
    this.currentSessionId = sessionId;
  }

  record(name: string, duration: number, persist = true) {
    const metric: LatencyMetric = {
      name,
      duration,
      timestamp: Date.now(),
      sessionId: this.currentSessionId || undefined
    };

    this.metrics.push(metric);

    // Keep only recent metrics
    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift();
    }

    console.log(`[Latency] ${name}: ${duration}ms`);

    // Queue for database persistence (batched)
    if (persist) {
      this.pendingPersistence.push(metric);
      this.schedulePersistence();
    }
  }

  private schedulePersistence() {
    // Batch writes every 5 seconds to avoid hammering DB
    if (this.persistenceTimer) return;
    
    this.persistenceTimer = setTimeout(() => {
      this.flushToDatabase();
      this.persistenceTimer = null;
    }, 5000);
  }

  private async flushToDatabase() {
    if (this.pendingPersistence.length === 0) return;

    const toFlush = [...this.pendingPersistence];
    this.pendingPersistence = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Group metrics by session for aggregation
      const sessionMetrics = new Map<string, Record<string, number>>();
      
      for (const metric of toFlush) {
        const sessionKey = metric.sessionId || 'default';
        if (!sessionMetrics.has(sessionKey)) {
          sessionMetrics.set(sessionKey, {});
        }
        const session = sessionMetrics.get(sessionKey)!;
        
        // Map metric names to database columns
        switch (metric.name) {
          case 'token_generation':
            session.token_gen_ms = metric.duration;
            break;
          case 'context_load':
            session.context_load_ms = metric.duration;
            break;
          case 'vad_trigger':
            session.vad_trigger_ms = metric.duration;
            break;
          case 'first_token':
            session.first_token_ms = metric.duration;
            break;
          case 'tool_execution':
            session.tool_exec_ms = metric.duration;
            break;
          case 'simple_command_match':
          case 'list_command_match':
            // Client-side command detection - should be <50ms
            session.first_token_ms = metric.duration;
            break;
          case 'list_command_exec':
            // Full list command execution - should be <100ms
            session.tool_exec_ms = metric.duration;
            break;
          case 'end_to_end_response':
          case 'webrtc_handshake':
          case 'connection_time':
            session.total_response_ms = metric.duration;
            break;
        }
      }

      // Insert aggregated metrics
      for (const [sessionId, metrics] of sessionMetrics) {
        const insertData = {
          user_id: user.id,
          session_id: sessionId === 'default' ? crypto.randomUUID() : sessionId,
          token_gen_ms: metrics.token_gen_ms ?? null,
          context_load_ms: metrics.context_load_ms ?? null,
          vad_trigger_ms: metrics.vad_trigger_ms ?? null,
          first_token_ms: metrics.first_token_ms ?? null,
          tool_exec_ms: metrics.tool_exec_ms ?? null,
          total_response_ms: metrics.total_response_ms ?? null,
          success: true,
        };
        
        await supabase
          .from('voice_latency_logs')
          .insert(insertData);
      }

      console.log(`[Latency] Persisted ${toFlush.length} metrics to database`);
    } catch (error) {
      console.warn('[Latency] Failed to persist metrics:', error);
      // Re-queue failed metrics
      this.pendingPersistence.push(...toFlush);
    }
  }

  getStats(name?: string) {
    const filtered = name 
      ? this.metrics.filter(m => m.name === name)
      : this.metrics;

    if (filtered.length === 0) {
      return null;
    }

    const durations = filtered.map(m => m.duration).sort((a, b) => a - b);
    const sum = durations.reduce((acc, val) => acc + val, 0);

    return {
      count: durations.length,
      min: durations[0],
      max: durations[durations.length - 1],
      mean: sum / durations.length,
      p50: durations[Math.floor(durations.length * 0.5)],
      p95: durations[Math.floor(durations.length * 0.95)],
      p99: durations[Math.floor(durations.length * 0.99)]
    };
  }

  getRecentMetrics(limit: number = 10) {
    return this.metrics.slice(-limit);
  }

  clear() {
    this.metrics = [];
  }

  logStats() {
    const metricNames = [...new Set(this.metrics.map(m => m.name))];
    
    console.log('\n=== Latency Statistics ===');
    metricNames.forEach(name => {
      const stats = this.getStats(name);
      if (stats) {
        console.log(`\n${name}:`);
        console.log(`  Count: ${stats.count}`);
        console.log(`  Mean: ${stats.mean.toFixed(2)}ms`);
        console.log(`  P50: ${stats.p50}ms`);
        console.log(`  P95: ${stats.p95}ms`);
        console.log(`  P99: ${stats.p99}ms`);
        console.log(`  Range: ${stats.min}ms - ${stats.max}ms`);
      }
    });
    console.log('=========================\n');
  }

  // Phase 11.3 Benchmark Summary
  getBenchmarkSummary(): {
    simpleCommandP95: number | null;
    listCommandP95: number | null;
    toolExecutionP95: number | null;
    e2eResponseP95: number | null;
    targets: { simple: number; list: number; tool: number; e2e: number };
    passing: { simple: boolean; list: boolean; tool: boolean; e2e: boolean };
  } {
    const targets = { simple: 50, list: 100, tool: 300, e2e: 500 };
    
    const simpleStats = this.getStats('simple_command_match');
    const listStats = this.getStats('list_command_exec');
    const toolStats = this.getStats('tool_execution');
    const e2eStats = this.getStats('end_to_end_response');
    
    const simpleP95 = simpleStats?.p95 ?? null;
    const listP95 = listStats?.p95 ?? null;
    const toolP95 = toolStats?.p95 ?? null;
    const e2eP95 = e2eStats?.p95 ?? null;
    
    return {
      simpleCommandP95: simpleP95,
      listCommandP95: listP95,
      toolExecutionP95: toolP95,
      e2eResponseP95: e2eP95,
      targets,
      passing: {
        simple: simpleP95 !== null && simpleP95 <= targets.simple,
        list: listP95 !== null && listP95 <= targets.list,
        tool: toolP95 !== null && toolP95 <= targets.tool,
        e2e: e2eP95 !== null && e2eP95 <= targets.e2e,
      }
    };
  }

  logBenchmarks() {
    const summary = this.getBenchmarkSummary();
    console.log('\n=== Phase 11.3 Benchmark Results ===');
    console.log(`Simple Commands: ${summary.simpleCommandP95 ?? 'N/A'}ms (target: <${summary.targets.simple}ms) ${summary.passing.simple ? '✅' : '❌'}`);
    console.log(`List Commands: ${summary.listCommandP95 ?? 'N/A'}ms (target: <${summary.targets.list}ms) ${summary.passing.list ? '✅' : '❌'}`);
    console.log(`Tool Execution: ${summary.toolExecutionP95 ?? 'N/A'}ms (target: <${summary.targets.tool}ms) ${summary.passing.tool ? '✅' : '❌'}`);
    console.log(`End-to-End: ${summary.e2eResponseP95 ?? 'N/A'}ms (target: <${summary.targets.e2e}ms) ${summary.passing.e2e ? '✅' : '❌'}`);
    console.log('=====================================\n');
  }

  // Force flush before page unload
  async forceFlush() {
    if (this.persistenceTimer) {
      clearTimeout(this.persistenceTimer);
      this.persistenceTimer = null;
    }
    await this.flushToDatabase();
  }
}

// Singleton instance
const monitor = new LatencyMonitor();

export const setLatencySessionId = (sessionId: string) => {
  monitor.setSessionId(sessionId);
};

export const recordLatency = (name: string, duration: number, persist = true) => {
  monitor.record(name, duration, persist);
};

export const getLatencyStats = (name?: string) => {
  return monitor.getStats(name);
};

export const logLatencyStats = () => {
  monitor.logStats();
};

export const logBenchmarks = () => {
  monitor.logBenchmarks();
};

export const getBenchmarkSummary = () => {
  return monitor.getBenchmarkSummary();
};

export const clearLatencyMetrics = () => {
  monitor.clear();
};

export const flushLatencyMetrics = async () => {
  await monitor.forceFlush();
};

// Auto-log stats every 5 minutes in development
if (import.meta.env.DEV) {
  setInterval(() => {
    monitor.logStats();
  }, 5 * 60 * 1000);
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    monitor.forceFlush();
  });
}
